/**
 * MilleHills Video Components
 * ─────────────────────────────────────────────────────────
 * VideoBackground — autoplay muted loop for section backgrounds
 * VideoReel       — click-to-play modal player with poster frame
 * useMediaSlot    — fetches a named video/image URL from the backend
 */
import { useState, useEffect, useRef } from 'react';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';
const API_URL   = process.env.REACT_APP_API_URL   || 'http://localhost:5002/api/v1';

// ── Hook: fetch a named media slot from backend ───────────
export function useMediaSlot(key) {
  const [url, setUrl]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!key) { setLoading(false); return; }
    fetch(`${API_URL}/pages/media-slots/${key}`)
      .then(r => r.json())
      .then(d => setUrl(d.data?.url || null))
      .catch(() => setUrl(null))
      .finally(() => setLoading(false));
  }, [key]);

  return { url, loading };
}

// ── Resolve a URL (absolute or relative to media service) ─
export function resolveMedia(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}

// ── VideoBackground ────────────────────────────────────────
/**
 * Props:
 *   src         string   — video URL (direct, skips slot lookup)
 *   slot        string   — named slot key (fetched from backend)
 *   poster      string   — poster image URL
 *   overlay     string   — CSS color for overlay (default: rgba(0,0,0,0.45))
 *   overlayGrad string   — CSS gradient override
 *   blur        number   — blur px (default: 0)
 *   style       object   — outer container style overrides
 *   children    node     — content rendered above the video
 */
export function VideoBackground({
  src, slot, poster, overlay, overlayGrad, blur = 0, style = {}, children,
}) {
  const videoRef = useRef();
  const [videoUrl, setVideoUrl] = useState(src ? resolveMedia(src) : null);
  const [loaded, setLoaded]     = useState(false);
  const [error, setError]       = useState(false);

  // Fetch slot if no direct src
  useEffect(() => {
    if (src) { setVideoUrl(resolveMedia(src)); return; }
    if (!slot) return;
    fetch(`${API_URL}/pages/media-slots/${slot}`)
      .then(r => r.json())
      .then(d => { if (d.data?.url) setVideoUrl(resolveMedia(d.data.url)); })
      .catch(() => {});
  }, [src, slot]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {});
  }, [videoUrl]);

  const overlayStyle = overlayGrad
    ? { background: overlayGrad }
    : { background: overlay || 'rgba(0,0,0,0.45)' };

  return (
    <div style={{ position: 'relative', overflow: 'hidden', ...style }}>
      {/* Video layer */}
      {videoUrl && !error && (
        <video
          ref={videoRef}
          src={videoUrl}
          poster={poster ? resolveMedia(poster) : undefined}
          autoPlay muted loop playsInline
          onCanPlay={() => setLoaded(true)}
          onError={() => setError(true)}
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            filter: blur ? `blur(${blur}px)` : undefined,
            opacity: loaded ? 1 : 0,
            transition: 'opacity 1s ease',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Overlay */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, ...overlayStyle }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        {children}
      </div>
    </div>
  );
}

// ── VideoReel ──────────────────────────────────────────────
/**
 * Props:
 *   src         string   — video URL
 *   slot        string   — named slot key
 *   poster      string   — poster/thumbnail image URL
 *   title       string   — reel title shown in modal
 *   label       string   — small label above play button
 *   aspectRatio string   — CSS aspect-ratio (default '16/9')
 *   borderRadius number  — default 16
 *   style       object
 */
export function VideoReel({
  src, slot, poster, title, label,
  aspectRatio = '16/9', borderRadius = 16, style = {},
}) {
  const [open, setOpen]       = useState(false);
  const [videoUrl, setVideoUrl] = useState(src ? resolveMedia(src) : null);
  const [posterUrl, setPosterUrl] = useState(poster ? resolveMedia(poster) : null);
  const [hover, setHover]     = useState(false);
  const videoRef = useRef();

  useEffect(() => {
    if (src) { setVideoUrl(resolveMedia(src)); return; }
    if (!slot) return;
    fetch(`${API_URL}/pages/media-slots/${slot}`)
      .then(r => r.json())
      .then(d => {
        if (d.data?.url)         setVideoUrl(resolveMedia(d.data.url));
        if (d.data?.poster_url)  setPosterUrl(resolveMedia(d.data.poster_url));
        if (d.data?.thumbnail_url && !posterUrl) setPosterUrl(resolveMedia(d.data.thumbnail_url));
      })
      .catch(() => {});
  }, [src, slot]);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => videoRef.current?.play().catch(() => {}), 100);
    return () => videoRef.current?.pause();
  }, [open]);

  if (!videoUrl) return null;

  return (
    <>
      {/* Trigger / thumbnail */}
      <div
        onClick={() => setOpen(true)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          position: 'relative', cursor: 'pointer',
          borderRadius, overflow: 'hidden',
          aspectRatio, background: 'var(--bg-2)',
          boxShadow: hover ? '0 20px 60px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.3)',
          transform: hover ? 'scale(1.01)' : 'scale(1)',
          transition: 'all 0.3s ease',
          ...style,
        }}
      >
        {/* Poster */}
        {posterUrl && (
          <img src={posterUrl} alt={title || 'Video'}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        )}

        {/* Dark overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          background: hover ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.45)',
          transition: 'background 0.3s',
        }} />

        {/* Play button */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 16,
        }}>
          {label && (
            <div style={{
              fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)',
            }}>{label}</div>
          )}
          <div style={{
            width: hover ? 76 : 68, height: hover ? 76 : 68,
            borderRadius: '50%',
            background: hover ? 'var(--gold)' : 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(8px)',
            border: `2px solid ${hover ? 'var(--gold)' : 'rgba(255,255,255,0.4)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.25s ease',
          }}>
            <div style={{
              width: 0, height: 0,
              borderTop: '12px solid transparent',
              borderBottom: '12px solid transparent',
              borderLeft: `20px solid ${hover ? '#080a0d' : '#fff'}`,
              marginLeft: 4,
              transition: 'border-color 0.25s',
            }} />
          </div>
          {title && (
            <div style={{
              fontSize: '0.85rem', fontWeight: 600, color: '#fff',
              textShadow: '0 1px 8px rgba(0,0,0,0.6)',
              maxWidth: '80%', textAlign: 'center',
            }}>{title}</div>
          )}
        </div>

        {/* Duration badge placeholder */}
        <div style={{
          position: 'absolute', bottom: 12, right: 12,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          borderRadius: 4, padding: '3px 8px',
          fontSize: '0.7rem', color: '#fff', fontWeight: 600,
        }}>▶ Play</div>
      </div>

      {/* Modal player */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9000,
            background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', animation: 'fadeIn 0.2s ease',
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            width: '100%', maxWidth: 960,
            borderRadius: 12, overflow: 'hidden',
            boxShadow: '0 40px 100px rgba(0,0,0,0.8)',
            position: 'relative',
          }}>
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              poster={posterUrl || undefined}
              style={{ width: '100%', display: 'block', background: '#000' }}
            />
            <button onClick={() => setOpen(false)} style={{
              position: 'absolute', top: 12, right: 12,
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: '#fff', fontSize: 16, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>✕</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── HoverVideoCard ─────────────────────────────────────────
/**
 * Wraps any card content — shows a looping video preview on hover
 * Props:
 *   videoUrl    string  — direct video URL
 *   poster      string  — poster/thumbnail
 *   children    node
 *   style       object
 */
export function HoverVideoCard({ videoUrl, poster, children, style = {}, ...rest }) {
  const [hover, setHover] = useState(false);
  const videoRef = useRef();
  const src = videoUrl ? resolveMedia(videoUrl) : null;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (hover) { v.currentTime = 0; v.play().catch(() => {}); }
    else v.pause();
  }, [hover]);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ position: 'relative', overflow: 'hidden', ...style }}
      {...rest}
    >
      {src && (
        <video
          ref={videoRef}
          src={src}
          muted loop playsInline
          poster={poster ? resolveMedia(poster) : undefined}
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            opacity: hover ? 1 : 0,
            transition: 'opacity 0.4s ease',
            pointerEvents: 'none', zIndex: 0,
          }}
        />
      )}
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  );
}